import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_GATE_PASSES_ADD,
    SOCIETY_PORTAL_GATE_PASSES_EDIT,
} from '../../../../Routing/routes';
import {
    getGatePassesList,
    revokeGatePass,
    deleteGatePass,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import useVmsList from '../useVmsList';
import useResidentPolicy from '../useResidentPolicy';
import VmsListShell from '../components/vms/VmsListShell';
import VmsTable from '../components/vms/VmsTable';
import VmsBadge from '../components/vms/VmsBadge';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import GateActionDialog from '../Visits/components/GateActionDialog';
import { labelCls } from '../components/form/formStyles';
import {
    GATE_PASS_STATUSES,
    GATE_PASS_TYPES,
    GATE_PASS_STATUS_STYLE,
    GATE_PASS_TYPE_STYLE,
    effectiveGatePassStatus,
    fmtDate,
    fmtTime,
    flatLabel,
    toOptions,
} from '../vmsConstants';
import { fmtPhone } from '../visitorConstants';

const STATUS_OPTIONS = toOptions(GATE_PASS_STATUSES);
const TYPE_OPTIONS = toOptions(GATE_PASS_TYPES);

const GatePasses = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const residentMayCreate = useResidentPolicy('allow_resident_gate_pass');
    const canCreate = can(PERMISSIONS.VMS_GATE_PASS_CREATE) && residentMayCreate;
    const canUpdate = can(PERMISSIONS.VMS_GATE_PASS_UPDATE);
    const canRevoke = can(PERMISSIONS.VMS_GATE_PASS_REVOKE);

    const acting = useSelector(state => state.VmsReducer?.gatePasses?.acting);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const list = useVmsList({
        slice: 'gatePasses',
        action: getGatePassesList,
        filters: { status: '', passType: '', wingId: '', flatId: '' },
    });

    const handleRevoke = (reason) => {
        dispatch(revokeGatePass({
            id: revokeTarget.gate_pass_id,
            reason: reason || undefined,
            onSuccess: () => { setRevokeTarget(null); list.refresh(); },
        }));
    };

    const handleDelete = () => {
        dispatch(deleteGatePass({
            id: deleteTarget.gate_pass_id,
            onSuccess: () => { setDeleteTarget(null); list.refresh(); },
        }));
    };

    // "Every day" and "any time" are the defaults, so the column says what is
    // actually restricted rather than listing seven day chips on every row.
    const scheduleLabel = (g) => {
        const days = Array.isArray(g.allowed_days) && g.allowed_days.length > 0
            ? g.allowed_days.join(', ')
            : 'Every day';
        const time = g.allowed_time_from && g.allowed_time_to
            ? `${fmtTime(g.allowed_time_from)}–${fmtTime(g.allowed_time_to)}`
            : 'Any time';
        return `${days} · ${time}`;
    };

    const columns = [
        {
            key: 'pass_code',
            header: 'Pass',
            nowrap: true,
            render: (g) => (
                <div>
                    <p className="tw-mb-0 tw-font-mono tw-font-semibold tw-text-gray-800">{g.pass_code || '—'}</p>
                    <VmsBadge value={g.pass_type} styles={GATE_PASS_TYPE_STYLE} className="tw-mt-0.5" />
                </div>
            ),
        },
        {
            key: 'visitor',
            header: 'Visitor',
            render: (g) => (
                <div className="tw-min-w-0">
                    <p className="tw-mb-0 tw-font-semibold tw-text-gray-800 tw-truncate">{g.visitor?.name || '—'}</p>
                    <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500 tw-font-mono">
                        {g.visitor?.phone ? fmtPhone(g.visitor.phone) : '—'}
                    </p>
                </div>
            ),
        },
        { key: 'flat', header: 'Flat', nowrap: true, render: (g) => flatLabel(g) },
        {
            key: 'window',
            header: 'Valid',
            nowrap: true,
            render: (g) => (
                <div className="tw-text-[10px] tw-leading-snug">
                    <p className="tw-mb-0">{fmtDate(g.valid_from)}</p>
                    <p className="tw-mb-0 tw-text-gray-500">to {fmtDate(g.valid_until)}</p>
                </div>
            ),
        },
        {
            key: 'schedule',
            header: 'Schedule',
            render: (g) => <span className="tw-text-[10px]">{scheduleLabel(g)}</span>,
        },
        {
            key: 'entries',
            header: 'Entries',
            render: (g) => (
                <span className="tw-inline-flex tw-items-center tw-justify-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-700 tw-font-semibold tw-text-[11px]">
                    {g.used_entries ?? 0}{g.max_entries ? `/${g.max_entries}` : ''}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (g) => {
                const status = effectiveGatePassStatus(g);
                return (
                    <VmsBadge
                        value={status}
                        styles={GATE_PASS_STATUS_STYLE}
                        title={status === 'REVOKED' ? g.revoke_reason : (
                            status === 'EXPIRED' && g.status === 'ACTIVE'
                                ? 'Past its window — the gate will skip it, though the record still reads ACTIVE'
                                : undefined
                        )}
                    />
                );
            },
        },
        {
            key: 'actions',
            header: 'Action',
            align: 'right',
            stopPropagation: true,
            render: (g) => (
                <div className="tw-inline-flex tw-items-center tw-gap-1">
                    {canUpdate && g.status === 'ACTIVE' && (
                        <button
                            title="Edit"
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_GATE_PASSES_EDIT.replace(':id', g.gate_pass_id))}
                            className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                        >
                            <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                        </button>
                    )}
                    {/* Revoke is only accepted while the pass is ACTIVE — the API
                        answers 400 otherwise. */}
                    {canRevoke && g.status === 'ACTIVE' && (
                        <button
                            title="Revoke"
                            disabled={acting}
                            onClick={() => setRevokeTarget(g)}
                            className="tw-p-1.5 tw-rounded hover:tw-bg-orange-50 tw-text-orange-600 disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:card-remove-outline" className="tw-text-base" />
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            title="Delete"
                            disabled={acting}
                            onClick={() => setDeleteTarget(g)}
                            className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600 disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    const filters = (
        <>
            <div>
                <label className={labelCls}>Status</label>
                <SearchableDropdown
                    small
                    value={list.filters.status}
                    onChange={(v) => list.setFilter('status', v === 'all' ? '' : v)}
                    options={STATUS_OPTIONS}
                    placeholder="All statuses"
                />
            </div>
            <div>
                <label className={labelCls}>Pass type</label>
                <SearchableDropdown
                    small
                    value={list.filters.passType}
                    onChange={(v) => list.setFilter('passType', v === 'all' ? '' : v)}
                    options={TYPE_OPTIONS}
                    placeholder="All types"
                />
            </div>
            <WingFlatPicker
                clearable
                required={false}
                societyId={list.societyId}
                wingId={list.filters.wingId}
                flatId={list.filters.flatId}
                onWingChange={(v) => list.setFilter('wingId', v)}
                onFlatChange={(v) => list.setFilter('flatId', v)}
                wingPlaceholder="Any wing"
                flatPlaceholder="Any flat"
            />
        </>
    );

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-slate-50 tw-border tw-border-slate-200 tw-text-[11px] tw-text-slate-700">
                <Icon icon="mdi:information-outline" className="tw-text-base tw-shrink-0 tw-mt-px" />
                <span>
                    Statuses account for the validity window. A pass past its end date is listed as <strong>Expired</strong> and
                    will be skipped at the gate, even though the stored status stays Active.
                </span>
            </div>

            <VmsListShell
                title="Gate Passes"
                subtitle="Standing authorisations for known visitors — domestic help, regular vendors and the like"
                itemLabel="gate passes"
                list={list}
                filters={filters}
                headerActions={canCreate && (
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_GATE_PASSES_ADD)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700"
                    >
                        <Icon icon="mdi:card-plus-outline" className="tw-text-base" />
                        Issue Gate Pass
                    </button>
                )}
            >
                <VmsTable
                    columns={columns}
                    rows={list.rows}
                    loading={list.loading}
                    loaderLabel="Loading gate passes..."
                    rowKey={(g) => g.gate_pass_id}
                    empty={{
                        icon: 'mdi:card-account-details-outline',
                        filtered: list.hasFilters,
                        filteredTitle: 'No gate passes match these filters',
                        emptyTitle: 'No gate passes issued yet',
                        emptyHint: 'A gate pass lets a known visitor in repeatedly without approval — bounded by dates, weekdays, a time window and an entry count.',
                        onClear: list.resetFilters,
                    }}
                />
            </VmsListShell>

            <GateActionDialog
                open={!!revokeTarget}
                title="Revoke this gate pass?"
                subtitle={revokeTarget ? `${revokeTarget.pass_code} · ${revokeTarget.visitor?.name || ''}` : ''}
                fieldLabel="Reason"
                fieldHint="optional, kept on the record"
                placeholder="e.g. Domestic help no longer employed"
                maxLength={255}
                multiline
                confirmLabel="Revoke pass"
                busyLabel="Revoking..."
                icon="mdi:card-remove-outline"
                tone="tw-bg-orange-600 hover:tw-bg-orange-700"
                busy={acting}
                onCancel={() => setRevokeTarget(null)}
                onConfirm={handleRevoke}
            />

            <ConfirmDialog
                open={!!deleteTarget}
                title="Delete this gate pass?"
                subtitle="It will stop working at the gate immediately."
                confirmLabel="Delete"
                busyLabel="Deleting..."
                tone="danger"
                icon="mdi:trash-can-outline"
                busy={acting}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
            >
                <p className="tw-mb-0">
                    <span className="tw-font-mono tw-font-semibold">{deleteTarget?.pass_code}</span> for{' '}
                    <span className="tw-font-semibold">{deleteTarget?.visitor?.name}</span> will be removed from the list.
                </p>
                {/* Delete only sets is_deleted — status is left untouched, so a
                    deleted pass still reads ACTIVE anywhere it is fetched
                    directly. Revoking is the honest way to end one. */}
                {deleteTarget?.status === 'ACTIVE' && (
                    <div className="tw-flex tw-items-start tw-gap-2 tw-px-2.5 tw-py-2 tw-mt-3 tw-rounded-md tw-bg-amber-50 tw-border tw-border-amber-100 tw-text-[10px] tw-text-amber-800">
                        <Icon icon="mdi:alert-outline" className="tw-text-sm tw-shrink-0 tw-mt-px" />
                        <span>
                            Deleting hides the pass but leaves its status as Active on the record, so nothing shows
                            <em> why</em> it ended. Revoke instead if you want the reason kept.
                        </span>
                    </div>
                )}
            </ConfirmDialog>
        </div>
    );
};

export default GatePasses;
